import React from 'react'
import styles from './IncentiveModal.module.scss'
import ToggleButtonGroup from '../../components/ToggleButtonGroup/ToggleButtonGroup'
import { useState, useEffect } from 'react'
import SettingPercentage from './SettingPercentage'
import { PlusIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'

const IncentiveModal = ({ isModalOpen, onFinish, onReturn, initialSettings }) => {
	const [isAlertMessageVisible, setIsAlertMessageVisible] = useState(false)
	const [incentiveTaxOption, setIncentiveTaxOption] = useState('amountDisplayed')
	const [incentiveSettings, setIncentiveSettings] = useState(() => {
		return initialSettings && initialSettings.length > 0
			? initialSettings
			: [{ id: 1, start: 1, end: 1, percentage: null }]
	})

	useEffect(() => {
		const newSettings =
			initialSettings && initialSettings.length > 0
				? initialSettings
				: [{ id: 1, start: 1, end: 1, percentage: null }]
		setIncentiveSettings(newSettings)
	}, [initialSettings])

	useEffect(() => {
		let timer

		if (isAlertMessageVisible) {
			timer = setTimeout(() => {
				setIsAlertMessageVisible(false)
			}, 3000)
		}

		return () => {
			clearTimeout(timer)
		}
	}, [isAlertMessageVisible])

	if (isModalOpen === false) {
		return null
	}

	const processPercentageInput = (inputVal, currentPercentage) => {
		let processedValue

		if (inputVal !== '' && !/^\d+$/.test(inputVal)) {
			return currentPercentage
		}

		if (inputVal === '') {
			processedValue = null
		} else {
			processedValue = Number(inputVal)

			if (processedValue > 100) {
				processedValue = 100
			} else if (processedValue < 1) {
				processedValue = 1
			}
		}
		return processedValue
	}

	const handleFirstPrizePercentageChange = (e) => {
		const firstPrizeSetting = incentiveSettings.find((s) => s.id === 1)
		if (!firstPrizeSetting) return

		const currentPercentage = firstPrizeSetting.percentage
		const processedValue = processPercentageInput(e.target.value, currentPercentage)

		updateSettingPercentage(firstPrizeSetting.id, processedValue)
	}

	const incentiveOption = [
		{ label: '상금 표기액', value: 'amountDisplayed' },
		{ label: '상금 실수령액', value: 'amountReceived' },
	]

	const updateSettingPercentage = (idToUpdate, newPercentage) => {
		setIncentiveSettings((prevSettings) => {
			return prevSettings.map((setting) => {
				if (setting.id === idToUpdate) {
					return { ...setting, percentage: newPercentage }
				}
				return setting
			})
		})
	}

	const updateSettingRank = (idToUpdate, newRank) => {
		setIncentiveSettings((prevSettings) => {
			return prevSettings.map((setting) => {
				if (setting.id === idToUpdate) {
					return { ...setting, end: newRank }
				}
				return setting
			})
		})
	}

	const handleAddSettings = () => {
		const lastSetting = incentiveSettings[incentiveSettings.length - 1]

		if (lastSetting.end === null || lastSetting.percentage === null) {
			setIsAlertMessageVisible(true)
			return
		}

		if (lastSetting.end === 'CUT' || lastSetting.end === 0) {
			return
		}

		setIncentiveSettings((prevSettings) => {
			const newId =
				prevSettings.length > 0
					? Math.max(...prevSettings.map((setting) => setting.id)) + 1
					: 1

			const newSetting = {
				id: newId,
				start: Number(lastSetting.end) + 1,
				end: null,
				percentage: null,
			}

			return [...prevSettings, newSetting]
		})
	}
	const handleDeleteSetting = (idToDelete) => {
		setIncentiveSettings((prevSettings) => {
			const indexToDelete = prevSettings.findIndex((s) => s.id === idToDelete)

			if (indexToDelete === -1 || prevSettings[indexToDelete].id === 1) {
				return prevSettings
			}

			const updatedSettings = prevSettings.filter((s) => s.id !== idToDelete)

			// 삭제된 항목이 중간에 있는 경우 (마지막 항목이 아닌 경우)
			if (indexToDelete < prevSettings.length - 1) {
				const deletedSettingStart = prevSettings[indexToDelete].start
				if (updatedSettings[indexToDelete]) {
					// 다음 항목이 존재하는지 확인
					updatedSettings[indexToDelete] = {
						...updatedSettings[indexToDelete],
						start: deletedSettingStart,
					}
				}
			}

			return updatedSettings
		})
	}

	const handleFinishClick = () => {
		// 우승 항목(id: 1)의 퍼센테지가 비어있는지 확인
		const firstPrizeSetting = incentiveSettings.find((s) => s.id === 1)
		if (firstPrizeSetting && firstPrizeSetting.percentage === null) {
			setIsAlertMessageVisible(true)
			return
		}
		let finalSettings = [...incentiveSettings]

		if (finalSettings.length > 1) {
			// 항목이 2개 이상일 때만 마지막 항목을 검사
			const lastSetting = finalSettings[finalSettings.length - 1]

			if (lastSetting.end === null || lastSetting.percentage === null) {
				finalSettings.pop()
			}
		}

		setIncentiveSettings(finalSettings)
		onFinish(incentiveTaxOption, finalSettings)
	}

	const lastEnd = incentiveSettings[incentiveSettings.length - 1]?.end
	const isAddButtonVisible = lastEnd !== 'CUT' && lastEnd !== 0

	// 완료 버튼 disabled 조건 체크
	const isFinishDisabled = () => {
		// 1. 우승 퍼센티지가 입력 안됬는지 확인
		const firstPrizeSetting = incentiveSettings.find((s) => s.id === 1)
		if (!firstPrizeSetting || firstPrizeSetting.percentage === null) {
			return true
		}

		// 2. 이후에 추가된 필드들이 전부 입력되었는지 확인
		const otherSettings = incentiveSettings.filter((s) => s.id !== 1)
		if (otherSettings.length > 0) {
			// 추가된 필드 중 하나라도 end나 percentage가 null이면 disabled
			const hasIncompleteField = otherSettings.some(
				(setting) => setting.end === null || setting.percentage === null,
			)
			if (hasIncompleteField) {
				return true
			}
		}

		return false
	}

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				<ToggleButtonGroup
					toggleFieldLabel="인센티브"
					toggleFieldVariable={incentiveTaxOption}
					toggleOption={incentiveOption}
					isNoticeNeed={true}
					isNoticeNeedText={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
					onChange={setIncentiveTaxOption}
					styleMode={styles}></ToggleButtonGroup>
				<div className={styles.prizeSettingContainer}>
					{incentiveSettings.map((setting, index) => {
						if (setting.id === 1) {
							return (
								<div className={styles.firstPrizeSetting} key={setting.id}>
									<div className={styles.settingsBox}>
										우승
										<div className={styles.inputWrapper}>
											<input
												type="text"
												onChange={handleFirstPrizePercentageChange}
												value={
													setting.percentage !== null
														? setting.percentage
														: ''
												}></input>
											<div className={styles.suffix}>%</div>
										</div>
									</div>
								</div>
							)
						} else {
							return (
								<SettingPercentage
									key={setting.id}
									id={setting.id}
									start={setting.start}
									end={setting.end}
									percentage={setting.percentage}
									onPercentageChange={updateSettingPercentage}
									onRankChange={updateSettingRank}
									onDelete={handleDeleteSetting}
									isRankEditable={
										index === incentiveSettings.length - 1
									}></SettingPercentage>
							)
						}
					})}
					{isAddButtonVisible && (
						<div className={styles.addSettings} onClick={handleAddSettings}>
							<PlusIcon className={styles.plusIcon}></PlusIcon>
							<div className={styles.addComment}>추가하기</div>
						</div>
					)}
				</div>
				<div className={styles.cancelFinish}>
					<button className={styles.cancel} onClick={onReturn} type="button">
						취소
					</button>
					<button
						className={styles.finish}
						onClick={handleFinishClick}
						type="button"
						disabled={isFinishDisabled()}>
						완료
					</button>
				</div>
			</div>
			{isAlertMessageVisible && (
				<div className={styles.alertMessageBox}>
					<ExclamationCircleIcon className={styles.warningIcon}></ExclamationCircleIcon>
					<p>필수 항목이 누락되었어요.</p>
					<p>다시 입력해주세요.</p>
				</div>
			)}
		</div>
	)
}

export default IncentiveModal
